import React, { useState, useEffect, useCallback } from "react";
import { Container, Card, Table, Button, Modal, Form, Alert, Badge } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaExclamationTriangle } from "react-icons/fa";
import { getAllUsersApi, createUserApi, updateUserApi, deleteUserApi } from "./service/api_service";
import { useAuth } from "../authen/AuthContext";
import { api } from "../../config/api";

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Modals state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Filter state
  const [showDeleted, setShowDeleted] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    username: "",
    password: "",
    display_name: "",
    role: "kitchen",
    allowed_branches: [],
    default_branch_id: 1
  });

  const token = localStorage.getItem("token");

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllUsersApi(token, showDeleted);
      setUsers(res.data);
      setError("");
    } catch (err) {
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [token, showDeleted]);

  const fetchBranches = useCallback(async () => {
    try {
      const res = await api.get("/api/branches");
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchBranches();
  }, [fetchUsers, fetchBranches]);

  const handleShowAdd = () => {
    setModalMode("add");
    setFormData({ id: null, username: "", password: "", display_name: "", role: "kitchen", allowed_branches: branches.map(b => b.id), default_branch_id: branches.length ? branches[0].id : 1 });
    setShowModal(true);
    setError("");
  };

  const handleShowEdit = (user) => {
    setModalMode("edit");
    setFormData({ 
      id: user.id, 
      username: user.username, 
      password: "", 
      display_name: user.display_name, 
      role: user.role,
      allowed_branches: user.allowed_branches || [],
      default_branch_id: user.default_branch_id || 1
    });
    setShowModal(true);
    setError("");
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowConfirmModal(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUserApi(token, userToDelete.id);
      setShowConfirmModal(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete user");
      setShowConfirmModal(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (modalMode === "add") {
        await createUserApi(token, formData);
      } else {
        await updateUserApi(token, formData.id, formData);
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  const roleColors = {
    master: "danger",
    admin: "primary",
    kitchen: "warning",
    rider: "info"
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h2 style={{ color: "#1e3c72", fontWeight: 600, margin: 0 }}>User Management</h2>
        
        <div className="d-flex align-items-center gap-3">
          {currentUser?.role === "master" && (
            <Form.Check 
              type="switch"
              id="show-deleted-switch"
              label="Show Deleted Users"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="fw-semibold text-secondary"
            />
          )}
          <Button 
            variant="primary" 
            style={{ background: "#1e3c72", border: "none", fontWeight: 600 }}
            onClick={handleShowAdd}
          >
            <FaPlus className="me-2" /> Add User
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Table responsive hover className="m-0">
            <thead style={{ background: "#f8f9fa" }}>
              <tr>
                <th className="px-4 py-3 border-0 text-muted">ID</th>
                <th className="px-4 py-3 border-0 text-muted">Username</th>
                <th className="px-4 py-3 border-0 text-muted">Display Name</th>
                <th className="px-4 py-3 border-0 text-muted">Role</th>
                <th className="px-4 py-3 border-0 text-muted">Branches</th>
                <th className="px-4 py-3 border-0 text-muted text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-4">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="5" className="text-center py-4">No users found</td></tr>
              ) : (
                users.map((user) => {
                  const isDeleted = !!user.deleted_at;
                  return (
                    <tr key={user.id} style={{ verticalAlign: "middle", opacity: isDeleted ? 0.6 : 1, backgroundColor: isDeleted ? "#fff5f5" : "inherit" }}>
                      <td className="px-4 py-3 fw-bold text-muted">#{user.id}</td>
                      <td className="px-4 py-3 text-secondary">
                        <span style={{ textDecoration: isDeleted ? "line-through" : "none" }}>{user.username}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span style={{ textDecoration: isDeleted ? "line-through" : "none" }}>{user.display_name}</span>
                        {isDeleted && <Badge bg="danger" className="ms-2">DELETED</Badge>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge bg={roleColors[user.role] || "secondary"} className="px-3 py-2 rounded-pill">
                          {user.role.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="d-flex flex-wrap gap-1">
                          {user.allowed_branches?.map(bid => {
                            const bName = branches.find(b => b.id === bid)?.name || `ID:${bid}`;
                            const isDefault = user.default_branch_id === bid;
                            return (
                              <Badge key={bid} bg={isDefault ? "primary" : "info"} className={isDefault ? "fw-bold" : "text-dark fw-normal"}>
                                {bName} {isDefault && "(Default)"}
                              </Badge>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-end">
                        {!isDeleted && (
                          <>
                            <Button variant="light" size="sm" className="me-2 text-primary" onClick={() => handleShowEdit(user)}>
                              <FaEdit />
                            </Button>
                            <Button variant="light" size="sm" className="text-danger" onClick={() => confirmDelete(user)}>
                              <FaTrash />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ color: "#1e3c72", fontWeight: 700 }}>
            {modalMode === "add" ? "Add New User" : "Edit User"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Username</Form.Label>
              <Form.Control 
                type="text" 
                required 
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Display Name</Form.Label>
              <Form.Control 
                type="text" 
                required 
                value={formData.display_name}
                onChange={(e) => setFormData({...formData, display_name: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">
                Password {modalMode === "edit" && <span className="text-muted fw-normal">(Leave blank to keep unchanged)</span>}
              </Form.Label>
              <Form.Control 
                type="password" 
                required={modalMode === "add"} 
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Role</Form.Label>
              <Form.Select 
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
              >
                <option value="kitchen">Kitchen</option>
                <option value="rider">Rider</option>
                <option value="admin">Admin</option>
                <option value="master">Master</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Allowed Branches</Form.Label>
              <div className="d-flex flex-wrap gap-3 p-3 bg-light rounded border">
                {branches.map(b => (
                  <Form.Check 
                    key={b.id}
                    type="checkbox"
                    id={`branch-${b.id}`}
                    label={b.name}
                    checked={formData.allowed_branches.includes(b.id)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => {
                        let newBranches = checked 
                          ? [...prev.allowed_branches, b.id] 
                          : prev.allowed_branches.filter(id => id !== b.id);
                        
                        let newDefault = prev.default_branch_id;
                        if (!newBranches.includes(newDefault)) {
                          newDefault = newBranches.length > 0 ? newBranches[0] : null;
                        }
                        
                        return { ...prev, allowed_branches: newBranches, default_branch_id: newDefault };
                      });
                    }}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary">Default Branch</Form.Label>
              <Form.Select 
                value={formData.default_branch_id || ""}
                onChange={(e) => setFormData({...formData, default_branch_id: Number(e.target.value)})}
                disabled={formData.allowed_branches.length === 0}
              >
                {formData.allowed_branches.map(bid => {
                  const bName = branches.find(b => b.id === bid)?.name || `ID:${bid}`;
                  return <option key={bid} value={bid}>{bName}</option>;
                })}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" style={{ background: "#1e3c72", border: "none" }}>
              {modalMode === "add" ? "Create User" : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="sm">
        <Modal.Body className="text-center p-4">
          <FaExclamationTriangle className="text-warning mb-3" size={48} />
          <h5 className="fw-bold mb-3">Delete User</h5>
          <p className="text-muted mb-4">
            Are you sure you want to delete <strong>{userToDelete?.username}</strong>? This action cannot be undone.
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Button variant="light" onClick={() => setShowConfirmModal(false)} className="px-4">Cancel</Button>
            <Button variant="danger" onClick={handleDelete} className="px-4">Delete</Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
}
